// ---------------------------------------------------------------------------
// 部門別 予算・実績対比表 レイアウト定義 (2026-06-12)
//   「PL作成ツール.xlsx」La SOFFITTA シート(店舗別損益 予算・実績対比書)を完全踏襲。
//   左パネル(A-J=単月) / 右パネル(L-U=累計) は同一の行構造・列構造で range のみ異なる。
//   行=la_soffitta_full.txt の B列(=M列)順 69項目。detail は freee 勘定科目名へマッピング、
//   subtotal は freee 決算書カテゴリー集計 or 派生式で算出。新規 freee 勘定科目は account_category_name
//   から所属 group を判定し subtotal 直前へ自動挿入(ロバスト・黙って落とさない)。
//
//   freee 勘定科目名↔表示項目名 対応は kanjo_taiou.txt(勘定奉行↔freee)由来:
//     材料仕入=仕入高 / 家賃=地代家賃 / 運賃=荷造運賃 / 交際接待費=交際費 等。
// ---------------------------------------------------------------------------

/** I列/J列(対前年差/対予算差)の意味は行種別で異なる(la_soffitta 実値で確定) */
export type DiffType =
  | 'amount'       // 金額差 (G-C, G-E)。detail 全行 + 大半の小計
  | 'achievement'  // 達成率% (G/C*100, G/E*100)。純売上高 のみ
  | 'ratio_point'; // 構成比ポイント差 (H-D, H-F)。売上原価 のみ

/** subtotal の値の出し方 */
export type SubtotalKind =
  | 'net_sales'      // 純売上高 = group「売上高」(actual) / sales detail 合算(budget)
  | 'cogs'           // 売上原価 = group「商品売上原価」(actual) / cost detail 合算(budget)
  | 'gross_profit'   // 売上総利益 = 純売上高 − 売上原価
  | 'labor'          // 人件費 = 役員報酬…募集費 detail 合算
  | 'selling'        // 販売経費 = 一般管理費 − 人件費
  | 'sga'            // 一般管理費 = group「販売管理費」(actual) / 販管費 detail 合算(budget)
  | 'operating'      // 営業利益 = 売上総利益 − 一般管理費
  | 'nonop_income'   // 営業外収益 = group「営業外収益」/ detail 合算
  | 'nonop_expense'  // 営業外費用 = group「営業外費用」/ detail 合算
  | 'ordinary'       // 経常利益 = 営業利益 + 営業外収益 − 営業外費用
  | 'pretax'         // 税引前当期利益 = 経常利益 + 特別利益 − 特別損失
  | 'store_ordinary';// 店舗経常利益 = 経常利益 − 本部経費

export interface BudgetLayoutRow {
  excelRow: number;          // la_soffitta Excel 行番号(トレーサビリティ)
  label: string;             // 表示科目名(la B列)
  kind: 'detail' | 'subtotal';
  /** detail: 値を引く freee 勘定科目名(account_item_name)。null=freee leaf 非対応(常に 0 or 親へ吸収) */
  freeeName?: string;
  /** detail: freee 決算書カテゴリー集計から引く場合のカテゴリー名(特別利益/特別損失 等の leaf 無し科目) */
  freeeCategory?: string;
  /** 予算 D1 の display_item キー(= label。予算が無い項目は undefined) */
  budgetItem?: string;
  /** subtotal: 値の算出種別 */
  subtotalKind?: SubtotalKind;
  /** 縦書きカテゴリー列(A/L)の1文字(la 実セル。無い行は undefined) */
  catChar?: string;
  diffType: DiffType;
  /** detail の字下げ(科目名) */
  indent: number;
  /** detail: net(closing-opening)でなく借方金額(gross)を actual/前年 に使う(社員割引=月末に福利厚生費へ振替されnet0になる対策) */
  grossDebit?: boolean;
  /** 累計パネルでは非表示(社員割引: 累計では福利厚生費に振替済みで重複するため) */
  excludeCumulative?: boolean;
}

// la_soffitta A列/L列 の縦書きカテゴリー文字(飛び飛び配置を厳密再現)
const CAT: Record<number, string> = {
  4: '収', 5: '営', 6: '業', 7: '収', 8: '益',     // 収益
  13: '売', 15: '上', 17: '原', 19: '価',          // 売上原価(飛び飛び)
  36: '一', 38: '般', 40: '管', 42: '理', 44: '費', // 販売費及び一般管理費
  60: '営', 61: '外', 62: '益',                     // 営業外収益
  64: '営', 65: '外', 66: '費',                     // 営業外費用
};

// 縦書きカテゴリーラベル: CAT の飛び飛び1文字配置を category 単位に束ねたもの。
//   行の表示/非表示で文字が欠けないよう、可視行スパンに「全文字」を中央配置(不足時は縦圧縮)で描く用。
export const VERTICAL_CATS: { label: string; minRow: number; maxRow: number }[] = [
  { label: '営業収益', minRow: 4, maxRow: 8 },   // 収益(各セルに保存される正しい文字列)
  { label: '売上原価', minRow: 13, maxRow: 19 },
  { label: '一般管理費', minRow: 36, maxRow: 44 },  // 販売費及び一般管理費
  { label: '営外益', minRow: 60, maxRow: 62 },      // 営業外収益
  { label: '営外費', minRow: 64, maxRow: 66 },      // 営業外費用
];

const D = (
  excelRow: number, label: string,
  opt: { freeeName?: string; freeeCategory?: string; budgetItem?: string; diffType?: DiffType } = {},
): BudgetLayoutRow => ({
  excelRow, label, kind: 'detail',
  freeeName: opt.freeeName, freeeCategory: opt.freeeCategory,
  budgetItem: opt.budgetItem, diffType: opt.diffType || 'amount',
  catChar: CAT[excelRow], indent: 1,
});
const S = (
  excelRow: number, label: string, subtotalKind: SubtotalKind, diffType: DiffType = 'amount',
): BudgetLayoutRow => ({
  excelRow, label, kind: 'subtotal', subtotalKind, diffType, catChar: CAT[excelRow], indent: 0,
});

// 69項目(la_soffitta 行4〜72)。budgetItem は予算 D1 に値がある項目のみ指定。
export const BUDGET_LAYOUT: BudgetLayoutRow[] = [
  // 収益
  D(4, '売上', { freeeName: '売上高', budgetItem: '売上' }),
  D(5, '本部売上'),
  D(6, 'ネット売上'),
  D(7, '社内転売売上'),
  D(8, '物販'),
  // 福利厚生費へ振替前の社員割引(借方グロス)を 営業収益の白行の最下部に表示(単月のみ・累計は福利厚生費と重複のため除外)。
  { excelRow: 8, label: '(社員割引)', kind: 'detail', freeeName: '社員割引', grossDebit: true, excludeCumulative: true, diffType: 'amount', indent: 1 },
  S(9, '純売上高', 'net_sales', 'achievement'),
  // 売上原価
  D(10, '期首棚卸高', { freeeName: '期首商品棚卸高' }),
  D(10, '包装材期首棚卸高', { freeeName: '包装材期首棚卸高' }),
  D(11, '材料仕入', { freeeName: '仕入高', budgetItem: '材料仕入' }),
  D(12, '煙草仕入'),
  D(13, 'ワイン仕入', { freeeName: 'ワイン仕入' }),
  D(14, 'ワイン仕入諸掛'),
  D(15, '本部仕入', { freeeName: '本部仕入' }),
  D(16, 'ワイン関連仕入'),
  D(17, '包装材仕入', { freeeName: '包装材仕入高' }),
  D(18, '社内仕入'),
  D(19, '期末棚卸高', { freeeName: '期末商品棚卸高' }),
  D(19, '包装材期末棚卸高', { freeeName: '包装材期末棚卸高' }),
  D(20, '他勘定振替', { freeeName: '他勘定振替高(商)' }),
  S(21, '売上原価', 'cogs', 'ratio_point'),
  S(22, '売上総利益', 'gross_profit'),
  // 人件費
  D(23, '役員報酬', { freeeName: '役員報酬' }),
  D(24, '給与手当', { freeeName: '給与手当', budgetItem: '給与手当' }),
  D(25, '雑給', { freeeName: '雑給', budgetItem: '雑給' }),
  D(26, 'インセンティブ', { freeeName: 'インセンティブ' }),
  D(27, '退職金'),
  D(28, '法定福利費', { freeeName: '法定福利費', budgetItem: '法定福利費' }),
  D(29, '福利厚生費', { freeeName: '福利厚生費', budgetItem: '福利厚生費' }),
  D(30, '通勤費', { freeeName: '通勤費', budgetItem: '通勤費' }),
  D(31, '研修費', { freeeName: '研修費', budgetItem: '研修費' }),
  D(32, '募集費', { freeeName: '募集費', budgetItem: '募集費' }),
  S(33, '人件費', 'labor'),
  // 販売費及び一般管理費(販売経費)
  D(34, '消耗品費', { freeeName: '消耗品費', budgetItem: '消耗品費' }),
  D(35, '事務用品費', { freeeName: '事務用品費', budgetItem: '事務用品費' }),
  D(36, '家賃', { freeeName: '市場家賃', budgetItem: '家賃' }),  // 2026-06-17 user指示: 家賃の実績は地代家賃ではなく市場家賃(864550592・市場家賃振替で各店配賦)で突合
  D(37, '賃借料', { freeeName: '賃借料', budgetItem: '賃借料' }),
  D(38, '保険料', { freeeName: '保険料', budgetItem: '保険料' }),
  D(39, '修繕費', { freeeName: '修繕費', budgetItem: '修繕費' }),
  D(40, '出張費', { freeeName: '出張費' }),
  D(41, '租税公課', { freeeName: '租税公課', budgetItem: '租税公課' }),
  D(42, '減価償却費', { freeeName: '減価償却費', budgetItem: '減価償却費' }),
  D(43, '旅費交通費', { freeeName: '旅費交通費' }),
  D(44, '通信費', { freeeName: '通信費', budgetItem: '通信費' }),
  D(45, '水道光熱費', { freeeName: '水道光熱費', budgetItem: '水道光熱費' }),
  D(46, '支払手数料', { freeeName: '支払手数料', budgetItem: '支払手数料' }),
  D(47, '運賃', { freeeName: '荷造運賃', budgetItem: '運賃' }),
  D(48, '広告宣伝費', { freeeName: '広告宣伝費', budgetItem: '広告宣伝費' }),
  D(49, '交際接待費', { freeeName: '交際費' }),
  D(50, '会議費', { freeeName: '会議費' }),
  D(51, '燃料費', { freeeName: '燃料費', budgetItem: '燃料費' }),
  D(52, '諸会費', { freeeName: '諸会費', budgetItem: '諸会費' }),
  D(53, '新聞図書費', { freeeName: '新聞図書費' }),
  D(54, '顧問料', { freeeName: '顧問料' }),
  D(55, '雑費', { freeeName: '雑費', budgetItem: '雑費' }),
  D(56, '寄付金', { freeeName: '寄付金' }),
  S(57, '販売経費', 'selling'),
  S(58, '一般管理費', 'sga'),
  S(59, '営業利益', 'operating'),
  // 営業外収益
  D(60, '受取利息', { freeeName: '受取利息' }),
  D(61, '受取配当金', { freeeName: '受取配当金' }),
  D(62, '雑収入', { freeeName: '雑収入' }),
  S(63, '営業外収益', 'nonop_income'),
  // 営業外費用
  D(64, '支払利息', { freeeName: '支払利息' }),
  D(65, '雑損失', { freeeName: '雑損失' }),
  S(66, '営業外費用', 'nonop_expense'),
  S(67, '経常利益', 'ordinary'),
  // 特別損益(leaf 無し科目は freee カテゴリー集計から)
  D(68, '特別利益', { freeeCategory: '特別利益' }),
  D(69, '特別損失', { freeeCategory: '特別損失' }),
  S(70, '税引前当期利益', 'pretax'),
  D(71, '本部経費'),
  S(72, '店舗経常利益', 'store_ordinary'),
];

// 各 container subtotal の構成 detail 行ラベル(budget の detail 合算 / actual の人件費 等に使う)
export const LABOR_LABELS = ['役員報酬', '給与手当', '雑給', 'インセンティブ', '退職金', '法定福利費', '福利厚生費', '通勤費', '研修費', '募集費'];
export const SELLING_LABELS = ['消耗品費', '事務用品費', '家賃', '賃借料', '保険料', '修繕費', '出張費', '租税公課', '減価償却費', '旅費交通費', '通信費', '水道光熱費', '支払手数料', '運賃', '広告宣伝費', '交際接待費', '会議費', '燃料費', '諸会費', '新聞図書費', '顧問料', '雑費', '寄付金'];
export const SALES_LABELS = ['売上', '本部売上', 'ネット売上', '社内転売売上', '物販'];
// 売上原価 detail と符号(期末棚卸高は控除=-1)
export const COGS_TERMS: { label: string; sign: 1 | -1 }[] = [
  { label: '期首棚卸高', sign: 1 }, { label: '包装材期首棚卸高', sign: 1 }, { label: '材料仕入', sign: 1 }, { label: '煙草仕入', sign: 1 },
  { label: 'ワイン仕入', sign: 1 }, { label: 'ワイン仕入諸掛', sign: 1 }, { label: '本部仕入', sign: 1 },
  { label: 'ワイン関連仕入', sign: 1 }, { label: '包装材仕入', sign: 1 }, { label: '社内仕入', sign: 1 },
  { label: '期末棚卸高', sign: -1 }, { label: '包装材期末棚卸高', sign: -1 }, { label: '他勘定振替', sign: 1 },
];
export const NONOP_INCOME_LABELS = ['受取利息', '受取配当金', '雑収入'];
export const NONOP_EXPENSE_LABELS = ['支払利息', '雑損失'];

// freee 決算書カテゴリー名(actual の container 集計に使う authoritative 値)
export const FREEE_GROUP = {
  netSales: '売上高',
  cogs: '商品売上原価',
  sga: '販売管理費',
  nonopIncome: '営業外収益',
  nonopExpense: '営業外費用',
  extraIncome: '特別利益',
  extraLoss: '特別損失',
} as const;

// freee account_category_name → 未マッピング leaf を寄せる container subtotal ラベル(ロバスト)
export const CATEGORY_TO_SUBTOTAL: { match: string; subtotal: string }[] = [
  { match: '売上高', subtotal: '純売上高' },
  { match: '期首商品棚卸', subtotal: '売上原価' },
  { match: '当期商品仕入', subtotal: '売上原価' },
  { match: '他勘定振替高(商)', subtotal: '売上原価' },
  { match: '期末商品棚卸', subtotal: '売上原価' },
  { match: '商品売上原価', subtotal: '売上原価' },
  { match: '販売管理費', subtotal: '販売経費' },
  { match: '営業外収益', subtotal: '営業外収益' },
  { match: '営業外費用', subtotal: '営業外費用' },
  { match: '特別利益', subtotal: '特別利益' },
  { match: '特別損失', subtotal: '特別損失' },
];

// la_soffitta タイトル(B1/M1)
export const BUDGET_TITLE = '80期上期損益 予算・実績対比書';
export const BUDGET_TITLE_CUM = '80期上期損益 予算・実績対比書（累計）';
